// src/components/WaveformAudioPlayer.js
//
// A compact waveform audio player (play button + animated waveform + time),
// built on wavesurfer.js (already a dependency, previously unused anywhere
// in this app). Reusable — pass any `src` to use it elsewhere.
import { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import '../css/waveformAudioPlayer.css';

function formatTime(seconds) {
  if (!isFinite(seconds) || seconds < 0) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function WaveformAudioPlayer({ src, accentColor = '#d9a441' }) {
  const containerRef = useRef(null);
  const wavesurferRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;

    const wavesurfer = WaveSurfer.create({
      container: containerRef.current,
      waveColor: 'rgba(255, 255, 255, 0.35)',
      progressColor: accentColor,
      cursorColor: 'transparent',
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      height: 22,
      normalize: true,
      url: src,
    });

    wavesurferRef.current = wavesurfer;

    wavesurfer.on('ready', () => {
      setDuration(wavesurfer.getDuration());
      setReady(true);
    });
    wavesurfer.on('timeupdate', (time) => setCurrentTime(time));
    wavesurfer.on('play', () => setIsPlaying(true));
    wavesurfer.on('pause', () => setIsPlaying(false));
    wavesurfer.on('finish', () => setIsPlaying(false));

    return () => {
      wavesurfer.destroy();
      wavesurferRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  function togglePlay() {
    wavesurferRef.current?.playPause();
  }

  return (
    <div className="waveform-player">
      <button
        type="button"
        className="waveform-player-btn"
        onClick={togglePlay}
        disabled={!ready}
        aria-label={isPlaying ? 'Pause audio preview' : 'Play audio preview'}
      >
        <i className={`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
      </button>

      <div className="waveform-player-wave" ref={containerRef}></div>

      <div className="waveform-player-time">
        {formatTime(isPlaying || currentTime > 0 ? currentTime : duration)}
      </div>
    </div>
  );
}

export default WaveformAudioPlayer;
